import { Injectable } from "@angular/core";
import {
  Http,
  RequestOptions,
  Response,
  Headers,
  RequestOptionsArgs
} from "@angular/http";
import { UtilService } from "../util";
import { AuthService } from "../auth";
import { Patient } from "../../models";
import { Observable } from "rxjs/Observable";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import "rxjs/add/observable/throw";
import "rxjs/add/observable/of";
import { PatientReg } from "app/shared/models/patient-reg.model";
import {
  headersToString,
  HttpResponse,
  HttpRequest
} from "selenium-webdriver/http";

/**
 * This class provides the Patient service with methods to read patients
 */
@Injectable()
export class PatientService {
  constructor(
    private http: Http,
    private utilService: UtilService,
    private authService: AuthService
  ) {}

  /**
   * Method to retreive all patients within the given searchCriteria.
   * @param searchCriteria - search, filter, orderBy
   * @returns {Observable<R|T>}
   */
  getPatients(searchCriteria: SearchCriteria): Observable<Array<Patient>> {
    let searchUrl: string = "/patients";
    let queryParams = this.utilService.setQueryParams({
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || []).map(patient => {
          return new Patient(patient);
        });
      })
      .catch(this.utilService.handleError);
  }

  getPatientListLength(): Observable<any> {
    return this.http
      .get("/patients")
      .map((res: Response) => {
        return res;
      });
  }

  getPatientsByLocation(
    locationId: number | string,
    searchCriteria: SearchCriteria
  ): Observable<Array<Patient>> {
    let searchUrl: string = "/location/" + locationId + "/patients";
    let queryParams = this.utilService.setQueryParams({
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      search: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(searchUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || {}).Patients || [];
      })
      .catch(this.utilService.handleError);
  }

  getPatientById(patientId: number | string): Observable<Patient> {
    let findUrl: string = "/patients/" + patientId;
    return this.http
      .get(findUrl)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  getPatientOrder(patientId: string, orderId: string): Observable<Order> {
    const url = `/encounters/${orderId}`;
    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getOrdersByPatient(
    patientId: number | string,
    searchCriteria: SearchCriteria = null
  ): Observable<Order[]> {
    let findUrl: string = "/patients/" + patientId + "/encounters";
    let queryObject: any = searchCriteria
      ? {
          sort: searchCriteria.Sort,
          order: searchCriteria.Order,
          q: searchCriteria.SearchTerm,
          page: searchCriteria.PageNumber
        }
      : {};
    let queryParams = this.utilService.setQueryParams(queryObject);
    let options = new RequestOptions({ params: queryParams });
    return this.http
      .get(findUrl, options)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getEncounterOrder(orderId: string): Observable<Order> {
    const url: string = `/encounters/${orderId}`;
    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }

  getReports(
    searchCriteria: SearchCriteria,
    physicianId?: number,
    locationId?: number | string
  ): Observable<Order[]> {
    let findUrl: string = "/encounters/reported";
    let queryObject: any = {
      sort: searchCriteria.Sort,
      order: searchCriteria.Order,
      q: searchCriteria.SearchTerm,
      page: searchCriteria.PageNumber
    };
    if (Boolean(locationId)) {
      queryObject.locationId = locationId;
    } else if (Boolean(physicianId)) {
      queryObject.physicianId = physicianId;
    }
    let queryParams = this.utilService.setQueryParams(queryObject);
    let options = new RequestOptions({ params: queryParams });
    return this.http
      .get(findUrl, options)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || [];
      })
      .catch(this.utilService.handleError);
  }


  getReportListLength(): Observable<any> {
    return this.http
      .get("/encounters/reported")
      .map((res: Response) => {
        return res;
      });
  }

  exists(patient: Patient): Observable<boolean> {
    let existsUrl: string = "/patients/exists";
    let queryParams = this.utilService.setQueryParams({
      email: patient.Email,
      id: patient.Id
    });
    let options = new RequestOptions({ params: queryParams });

    return this.http
      .get(existsUrl, options)
      .map((res: Response) => {
        return (this.utilService.outputParser(res) || {}).Exists || false;
      })
      .catch(this.utilService.handleError);
  }

  createPatient(patient: Patient): Observable<Patient> {
    let url: string = "/patients";

    return this.http
      .post(url, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  sendActivationEmail(patient: Patient) {
    let url: string = "/patients/" + patient.Id + "/activate";

    return this.http
      .post(url, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  completeRegistration(patientReg: PatientReg): Observable<any> {
    let url: string = `/users/${patientReg.UserId}/password`;

    return this.http
      .post(url, patientReg)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientProfile(patient: Patient): Observable<Object> {
    let updateUrl: string = "/patients/" + patient.Id;
    return this.http
      .put(updateUrl, patient)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientBilling(patient: Patient): Observable<Object> {
    let updateUrl: string = "/patients/" + patient.Id + "/billing";
    return this.http
      .put(updateUrl, {
        BillTo: patient.BillTo,
        BillToText: patient.BillToText,
        Provider: patient.Provider,
        PolicyId: patient.PolicyId,
        GroupNumber: patient.GroupNumber,
        DoctorId: patient.DoctorId
      })
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  updatePatientPhysician(patient: Patient): Observable<Object> {
    let updateUrl: string =
      "/patients/" + patient.Id + "/physician/" + patient.DoctorId;
    return this.http
      .put(updateUrl, {})
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }

  getLatestTestResults(patientId: number) {
    let url = "/patients/" + patientId + "/test-results";
    return this.http
      .get(url)
      .map((res: Response) => {
        return this.utilService.outputParser(res) || {};
      })
      .catch(this.utilService.handleError);
  }
}



// WEBPACK FOOTER //
// /Users/maxsheikhizadeh/Desktop/GEN/physician-portal/src/app/shared/services/patient/patient.service.ts